import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import {
  Search, Boxes, AlertOctagon, Stethoscope, FileText, ChevronRight, Plus, RefreshCw, Link2,
} from "lucide-react";
import AppShell from "@/components/AppShell";
import { api, formatApiError } from "@/lib/api";

const TABS = [
  { key: "search", label: "Knowledge Search", icon: Search },
  { key: "components", label: "Component Registry", icon: Boxes },
  { key: "incidents", label: "Incident Library", icon: AlertOctagon },
  { key: "diagnostics", label: "Diagnostics", icon: Stethoscope },
];
const SEV = {
  informational: "bg-slate-100 text-slate-600", warning: "bg-amber-100 text-amber-700",
  likely_cause: "bg-orange-100 text-orange-700", confirmed_cause: "bg-red-100 text-red-700",
  critical: "bg-red-100 text-red-700", high: "bg-green-100 text-green-700",
  medium: "bg-blue-100 text-blue-700", confirmed: "bg-green-100 text-green-700", likely: "bg-amber-100 text-amber-700",
};
const decode = (e) => {
  const d = e?.response?.data?.detail;
  if (d && typeof d === "object" && d.error === "mfa_required") return "MFA required — complete multi-factor auth before this action.";
  return formatApiError(d);
};

export default function SystemIntelligence() {
  const [tab, setTab] = useState("search");
  return (
    <AppShell title="System Intelligence Center" subtitle="Internal searchable knowledge, diagnostics & incident memory (Preview — read-only, recommend-only).">
      <div className="space-y-5" data-testid="system-intelligence">
        <div className="flex gap-2 flex-wrap border-b border-slate-200 pb-2">
          {TABS.map((t) => (
            <button key={t.key} onClick={() => setTab(t.key)} data-testid={`si-tab-${t.key}`}
              className={`flex items-center gap-1.5 px-3 py-2 rounded-md text-sm font-medium ${tab === t.key ? "bg-[#0A0F1C] text-white" : "text-slate-600 hover:bg-slate-100"}`}>
              <t.icon size={15} /> {t.label}
            </button>
          ))}
        </div>
        {tab === "search" && <SearchTab />}
        {tab === "components" && <ComponentsTab />}
        {tab === "incidents" && <IncidentsTab />}
        {tab === "diagnostics" && <DiagnosticsTab />}
      </div>
    </AppShell>
  );
}

function SearchTab() {
  const [q, setQ] = useState("");
  const [res, setRes] = useState(null);
  const [loading, setLoading] = useState(false);
  const run = async () => {
    if (!q.trim()) return;
    setLoading(true);
    try {
      const { data } = await api.get("/admin/system-intelligence/search", { params: { q } });
      setRes(data);
    } catch (e) { toast.error(decode(e)); } finally { setLoading(false); }
  };
  const examples = ["Why is the Security Dashboard returning 403?", "Where is the MFA enforcement configuration located?",
    "Which tests validate the audit hash chain?", "What remains simulated in TradeGuard?"];
  return (
    <div className="space-y-4" data-testid="si-search">
      <div className="flex gap-2">
        <input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && run()}
          data-testid="si-search-input" placeholder="Ask about a feature, error, API, test, incident…"
          className="flex-1 h-11 rounded-md border border-slate-200 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#2563EB]" />
        <button onClick={run} disabled={loading} data-testid="si-search-btn"
          className="h-11 px-5 rounded-md bg-[#2563EB] text-white text-sm font-semibold disabled:opacity-60">
          {loading ? "Searching…" : "Search"}
        </button>
      </div>
      <div className="flex flex-wrap gap-2">
        {examples.map((ex) => (
          <button key={ex} onClick={() => setQ(ex)} className="text-xs px-2 py-1 rounded-full border border-slate-200 text-slate-500 hover:bg-slate-50">{ex}</button>
        ))}
      </div>
      {res && (
        <div className="space-y-3" data-testid="si-search-results">
          <div className="border border-slate-200 rounded-lg p-4 bg-slate-50">
            <div className="text-[10px] uppercase tracking-wider text-slate-400 mb-1">Answer</div>
            <div className="text-sm text-[#0A0F1C] whitespace-pre-wrap" data-testid="si-answer">{res.answer}</div>
            {res.primary_source && (
              <div className="text-xs text-slate-500 mt-2 flex items-center gap-2">
                <FileText size={13} /> Source: <b>{res.primary_source}</b>{res.primary_section ? ` · ${res.primary_section}` : ""}
                {res.confidence && <span className={`px-2 py-0.5 rounded-full text-[10px] ${SEV[res.confidence] || "bg-slate-100"}`}>{res.confidence}</span>}
              </div>
            )}
            {res.disclaimer && <div className="text-[11px] text-slate-400 mt-2">{res.disclaimer}</div>}
          </div>
          {(res.results || []).map((r, i) => (
            <div key={i} className="border border-slate-100 rounded-md p-3" data-testid={`si-result-${i}`}>
              <div className="flex items-center justify-between">
                <div className="font-medium text-sm text-[#0A0F1C]">{r.title}</div>
                <span className={`px-2 py-0.5 rounded-full text-[10px] ${SEV[r.confidence] || "bg-slate-100"}`}>{r.kind} · {r.confidence}</span>
              </div>
              <div className="text-xs text-slate-500 mt-1 line-clamp-2">{r.answer}</div>
              <div className="text-[11px] text-slate-400 mt-1">Source: {r.source}{r.file ? ` · ${r.file}` : ""}{r.phase ? ` · phase ${r.phase}` : ""}</div>
              {r.troubleshooting?.length > 0 && (
                <ul className="text-[11px] text-slate-500 mt-1 list-disc ml-4">{r.troubleshooting.map((t, j) => <li key={j}>{t}</li>)}</ul>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ComponentsTab() {
  const [rows, setRows] = useState([]);
  const [type, setType] = useState("");
  const [types, setTypes] = useState([]);
  const [sel, setSel] = useState(null);
  const load = useCallback(() => {
    api.get("/admin/system-intelligence/components", { params: type ? { type } : {} })
      .then(({ data }) => { setRows(data.components); setTypes(data.types); })
      .catch((e) => toast.error(decode(e)));
  }, [type]);
  useEffect(() => { load(); }, [load]);
  return (
    <div className="space-y-3" data-testid="si-components">
      <select value={type} onChange={(e) => setType(e.target.value)} data-testid="si-component-type"
        className="h-9 px-2 rounded-md border border-slate-200 text-sm">
        <option value="">{`All types (${rows.length})`}</option>
        {types.map((t) => <option key={t} value={t}>{t}</option>)}
      </select>
      <div className="border border-slate-200 rounded-lg divide-y divide-slate-100 max-h-[520px] overflow-y-auto">
        {rows.slice(0, 300).map((c) => (
          <button key={c.component_id} onClick={() => setSel(c.component_id)} data-testid={`si-comp-${c.component_id}`}
            className="w-full text-left px-4 py-2.5 hover:bg-slate-50 flex items-center justify-between">
            <div>
              <div className="text-sm text-[#0A0F1C]">{c.name}</div>
              <div className="text-[11px] text-slate-400">{c.type}{c.introduced_phase ? ` · ${c.introduced_phase}` : ""}{c.endpoint ? ` · ${c.endpoint}` : ""}</div>
            </div>
            <ChevronRight size={15} className="text-slate-300" />
          </button>
        ))}
      </div>
      {sel && <ComponentDrawer id={sel} onClose={() => setSel(null)} />}
    </div>
  );
}

function ComponentDrawer({ id, onClose }) {
  const [d, setD] = useState(null);
  useEffect(() => { api.get(`/admin/system-intelligence/components/${id}`).then(({ data }) => setD(data)).catch(() => {}); }, [id]);
  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/40" onClick={onClose} data-testid="si-component-drawer">
      <div className="w-full max-w-lg h-full bg-white overflow-y-auto p-6 space-y-4" onClick={(e) => e.stopPropagation()}>
        {!d ? <div className="text-slate-400">Loading…</div> : (
          <>
            <div className="font-heading font-bold text-lg text-[#0A0F1C]">{d.component.name}</div>
            <div className="text-xs text-slate-400">{d.component.type} · {d.component.status}{d.component.introduced_phase ? ` · introduced ${d.component.introduced_phase}` : ""}</div>
            {d.component.purpose && <p className="text-sm text-slate-600">{d.component.purpose}</p>}
            {d.component.file_path && <div className="text-xs"><b>File:</b> <span className="font-mono">{d.component.file_path}</span></div>}
            {d.component.endpoint && <div className="text-xs"><b>Endpoint:</b> <span className="font-mono">{d.component.endpoint}</span></div>}
            {d.component.dependencies?.length > 0 && (
              <div className="text-xs"><b className="flex items-center gap-1"><Link2 size={13} /> Depends on:</b> {d.component.dependencies.join(", ")}</div>
            )}
            {d.dependents?.length > 0 && (
              <div className="text-xs"><b>Used by:</b> {d.dependents.map((x) => x.name).join(", ")}</div>
            )}
            {d.component.related_tests?.length > 0 && <div className="text-xs"><b>Validated by:</b> {d.component.related_tests.join(", ")}</div>}
            {d.component.troubleshooting?.length > 0 && (
              <div><b className="text-xs">Troubleshooting:</b><ul className="text-xs text-slate-600 list-disc ml-5 mt-1">{d.component.troubleshooting.map((t, i) => <li key={i}>{t}</li>)}</ul></div>
            )}
            {d.related_incidents?.length > 0 && <div className="text-xs"><b>Incidents:</b> {d.related_incidents.map((i) => i.title).join("; ")}</div>}
          </>
        )}
      </div>
    </div>
  );
}

function IncidentsTab() {
  const [rows, setRows] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ title: "", root_cause: "", resolution: "", symptoms: "", affected_components: "", linked_phase: "" });
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => { api.get("/admin/system-intelligence/incidents").then(({ data }) => setRows(data.incidents)).catch((e) => toast.error(decode(e))); }, []);
  useEffect(() => { load(); }, [load]);
  const submit = async () => {
    if (!form.title.trim() || !form.root_cause.trim()) return toast.error("Title and root cause are required.");
    setBusy(true);
    try {
      await api.post("/admin/system-intelligence/incidents", {
        ...form, affected_components: form.affected_components.split(",").map((s) => s.trim()).filter(Boolean),
      });
      toast.success("Incident saved to the knowledge library.");
      setShowForm(false); setForm({ title: "", root_cause: "", resolution: "", symptoms: "", affected_components: "", linked_phase: "" }); load();
    } catch (e) { toast.error(decode(e)); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-3" data-testid="si-incidents">
      <button onClick={() => setShowForm(!showForm)} data-testid="si-incident-new"
        className="h-9 px-3 rounded-md bg-[#0A0F1C] text-white text-sm flex items-center gap-1.5"><Plus size={15} /> Record incident</button>
      {showForm && (
        <div className="border border-slate-200 rounded-lg p-4 space-y-2" data-testid="si-incident-form">
          <div className="text-xs text-amber-600">Recording requires an MFA-verified session (senior role). Fields are append-only & versioned.</div>
          {["title", "symptoms", "root_cause", "resolution", "affected_components", "linked_phase"].map((f) => (
            <input key={f} value={form[f]} onChange={(e) => setForm({ ...form, [f]: e.target.value })} data-testid={`si-incident-${f}`}
              placeholder={f.replace(/_/g, " ") + (["title", "root_cause"].includes(f) ? " (required)" : "")}
              className="w-full h-9 rounded-md border border-slate-200 px-2 text-sm" />
          ))}
          <button onClick={submit} disabled={busy} data-testid="si-incident-save" className="h-9 px-4 rounded-md bg-[#16A34A] text-white text-sm disabled:opacity-60">{busy ? "Saving…" : "Save incident"}</button>
        </div>
      )}
      <div className="border border-slate-200 rounded-lg divide-y divide-slate-100">
        {rows.length === 0 ? <div className="p-6 text-center text-slate-300 text-sm">No incidents recorded yet.</div> :
          rows.map((i) => (
            <div key={i.incident_id} className="px-4 py-3" data-testid={`si-incident-row-${i.incident_id}`}>
              <div className="flex items-center justify-between">
                <div className="font-medium text-sm text-[#0A0F1C]">{i.title}</div>
                <span className="text-[11px] text-slate-400">v{i.version} · {i.status}{i.linked_phase ? ` · ${i.linked_phase}` : ""}</span>
              </div>
              <div className="text-xs text-slate-500 mt-1"><b>Root cause:</b> {i.root_cause}</div>
              {i.resolution && <div className="text-xs text-slate-500"><b>Resolution:</b> {i.resolution}</div>}
              <div className="text-[11px] text-slate-400 mt-1">by {i.entered_by}{i.reviewed_by ? ` · reviewed by ${i.reviewed_by}` : " · pending review"}</div>
            </div>
          ))}
      </div>
    </div>
  );
}

function DiagnosticsTab() {
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(false);
  const run = useCallback(() => {
    setLoading(true);
    api.get("/admin/system-intelligence/diagnostics").then(({ data }) => setD(data)).catch((e) => toast.error(decode(e))).finally(() => setLoading(false));
  }, []);
  useEffect(() => { run(); }, [run]);
  return (
    <div className="space-y-3" data-testid="si-diagnostics">
      <button onClick={run} disabled={loading} data-testid="si-diag-run" className="h-9 px-3 rounded-md border border-slate-200 text-sm flex items-center gap-1.5"><RefreshCw size={14} className={loading ? "animate-spin" : ""} /> Run diagnostics</button>
      {d && (
        <>
          <div className="text-[11px] text-slate-400">{d.note}</div>
          <div className="border border-slate-200 rounded-lg divide-y divide-slate-100">
            {d.findings.map((f, i) => (
              <div key={i} className="px-4 py-3" data-testid={`si-finding-${f.check}`}>
                <div className="flex items-center justify-between">
                  <div className="text-sm text-[#0A0F1C]">{f.check.replace(/_/g, " ")}</div>
                  <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${SEV[f.severity] || "bg-slate-100"}`}>{f.severity}</span>
                </div>
                <div className="text-xs text-slate-500 mt-1 font-mono break-words">{typeof f.detail === "object" ? JSON.stringify(f.detail) : String(f.detail)}</div>
                {f.recommended_action && <div className="text-[11px] text-blue-600 mt-1">Recommended: {f.recommended_action}</div>}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
